window.ttx = window.ttx || {};
ttx.shortcut = {};

// ============================================================
// 快捷键系统
// 参考 MDF cb.registerAction / cb.shortcut API
// 全局键盘监听器只安装一次（_globalListenerInstalled 守卫）
// 所有日志输出走 ttx.console.*，不使用原生 console
// ============================================================

// 全局快捷键注册表：每项 { id, keys, normalizedKeys, callback, viewmodel, options, name }
var _shortcutRegistry = [];
// 全局键盘监听器是否已安装（确保只安装一次）
var _globalListenerInstalled = false;
// 自增 id，用于唯一标识每条注册项
var _shortcutSeed = 1;
// 配置项注册表：viewmodel -> { unfocusFirstElement, focusTipsFirstElement }
var _shortcutConfig = {};
// 防抖计时器缓存：normalizedKeys -> timer（仅对开启 debounce 的项生效）
var _debounceTimers = {};

// 修饰键集合（归一化后均为小写）
var MODIFIER_KEYS = { ctrl: 1, control: 1, shift: 1, alt: 1, meta: 1, cmd: 1, command: 1 };
// 修饰键的规范顺序（用于归一化时排序，确保注册与事件匹配顺序一致）
var MODIFIER_ORDER = ['ctrl', 'shift', 'alt', 'meta'];

/**
 * 归一化快捷键字符串：
 *   'ctrl + y' -> 'ctrl+y'
 *   'Alt+N'    -> 'alt+n'
 *   'ctrl+shift+f' -> 'ctrl+shift+f'
 * 同时把 cmd/command/meta 统一成 ctrl（Mac 兼容：cmd 键当 ctrl 处理），
 * 并把修饰键按规范顺序排列，主键放最后。
 * @param {string} keys 原始快捷键字符串
 * @returns {string} 归一化后的标识
 */
function normalizeKeys(keys) {
    if (typeof keys !== 'string' || !keys.trim()) return '';
    // 按 + 分割，去掉所有空格，统一小写
    var parts = keys.toLowerCase().replace(/\s+/g, '').split('+').filter(Boolean);
    if (!parts.length) return '';

    var modifiers = [];
    var mainKeys = [];
    for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        if (MODIFIER_KEYS[p]) {
            // cmd/command/meta 统一当作 ctrl 处理（Mac 兼容）
            if (p === 'cmd' || p === 'command' || p === 'meta') {
                if (modifiers.indexOf('ctrl') === -1) modifiers.push('ctrl');
            } else if (p === 'control') {
                if (modifiers.indexOf('ctrl') === -1) modifiers.push('ctrl');
            } else {
                if (modifiers.indexOf(p) === -1) modifiers.push(p);
            }
        } else {
            mainKeys.push(p);
        }
    }
    // 修饰键按规范顺序排列
    modifiers.sort(function (a, b) {
        return MODIFIER_ORDER.indexOf(a) - MODIFIER_ORDER.indexOf(b);
    });
    return modifiers.concat(mainKeys).join('+');
}

/**
 * 从键盘事件构造归一化快捷键标识
 * Mac 兼容：metaKey（cmd）当 ctrl 处理
 * @param {KeyboardEvent} e
 * @returns {string} 归一化后的标识（仅修饰键按下时返回空字符串）
 */
function eventToKeys(e) {
    var modifiers = [];
    // ctrlKey 或 metaKey（Mac cmd）都视为 ctrl
    if (e.ctrlKey || e.metaKey) modifiers.push('ctrl');
    if (e.shiftKey) modifiers.push('shift');
    if (e.altKey) modifiers.push('alt');

    // 仅为修饰键时不参与匹配
    var key = e.key;
    if (!key) return '';
    var lower = key.toLowerCase();
    if (lower === 'control' || lower === 'shift' || lower === 'alt' || lower === 'meta') {
        return '';
    }
    return modifiers.concat([lower]).join('+');
}

/**
 * 安装全局 keydown 监听器（仅安装一次）
 * 监听 document 的 keydown 事件，匹配注册表中的快捷键并触发回调
 */
function installGlobalListener() {
    if (_globalListenerInstalled) return;
    if (typeof document === 'undefined' || !document.addEventListener) return;
    _globalListenerInstalled = true;

    document.addEventListener('keydown', function (e) {
        var keys = eventToKeys(e);
        if (!keys) return;

        // 倒序遍历，后注册的优先触发；收集所有匹配项
        var matched = [];
        for (var i = _shortcutRegistry.length - 1; i >= 0; i--) {
            var item = _shortcutRegistry[i];
            if (item.normalizedKeys === keys) {
                matched.push(item);
            }
        }
        if (!matched.length) return;

        // 取第一个匹配项触发（避免同 keys 多回调重复 preventDefault）
        var target = matched[0];
        var opts = target.options || {};

        // 条件函数：返回 false 则跳过
        if (typeof opts.condition === 'function' && !opts.condition()) {
            return;
        }

        // 阻止默认事件 / 禁止冒泡
        if (opts.preventDefault !== false) {
            e.preventDefault();
        }
        if (opts.stopPropagation === true) {
            e.stopPropagation();
        }

        var fire = function () {
            try {
                target.callback(e, opts.params);
            } catch (err) {
                ttx.console.error('shortcut callback error', err && err.message ? err.message : err);
            }
        };

        // 防抖处理（250ms）
        if (opts.debounce === true) {
            var k = target.normalizedKeys;
            if (_debounceTimers[k]) {
                clearTimeout(_debounceTimers[k]);
            }
            _debounceTimers[k] = setTimeout(function () {
                _debounceTimers[k] = null;
                fire();
            }, 250);
        } else {
            fire();
        }
    });

    ttx.console.log('[shortcut] 全局键盘监听器已安装');
}

/**
 * 注册快捷键
 * @param {string} keys 快捷键字符串，如 'ctrl + y'、'alt+n'、'ctrl+shift+f'
 * @param {function} callback 回调函数 function(e, params)
 * @param {object} [viewmodel] 关联的 ViewModel（用于作用域隔离）
 * @param {object} [options] 配置项
 *   - preventDefault {boolean} 阻止默认事件
 *   - stopPropagation {boolean} 禁止冒泡
 *   - debounce {boolean} 防抖（250ms）
 *   - condition {function} 条件函数，返回 false 时不触发
 *   - showName {string} 快捷键中文名（用于帮助弹窗）
 *   - groupId {number} 分组 id（0-4）
 *   - params {any} 额外参数，会透传给 callback 的第二个参数
 * @returns {string} 注册项 id
 */
ttx.shortcut.registerAction = function (keys, callback, viewmodel, options) {
    if (typeof keys !== 'string' || typeof callback !== 'function') {
        ttx.console.warn('[shortcut] registerAction 参数无效：keys 必须为字符串，callback 必须为函数');
        return null;
    }
    var normalizedKeys = normalizeKeys(keys);
    if (!normalizedKeys) {
        ttx.console.warn('[shortcut] registerAction 快捷键解析失败：' + keys);
        return null;
    }

    var opts = options || {};
    var name = opts.showName || callback.name || '';
    var id = 'tx-shortcut-' + (_shortcutSeed++);

    _shortcutRegistry.push({
        id: id,
        keys: keys,
        normalizedKeys: normalizedKeys,
        callback: callback,
        viewmodel: viewmodel || null,
        options: opts,
        name: name
    });

    // 同时在 ttx.command 中注册同名事件（keys 解析后的标识），以便通过命令触发
    if (ttx.command && typeof ttx.command.register === 'function') {
        (function (item) {
            ttx.command.register(item.normalizedKeys, function (data) {
                var d = data || {};
                try {
                    item.callback(d.event || {}, d.params !== undefined ? d.params : item.options.params);
                } catch (err) {
                    ttx.console.error('shortcut command error', err && err.message ? err.message : err);
                }
            });
        })(_shortcutRegistry[_shortcutRegistry.length - 1]);
    }

    // 注册后触发 viewmodel 的 afterShortcutRegisterAction 事件
    if (viewmodel && typeof viewmodel.execute === 'function') {
        try {
            viewmodel.execute('afterShortcutRegisterAction', { keys: keys, normalizedKeys: normalizedKeys });
        } catch (err) {
            ttx.console.error('[shortcut] afterShortcutRegisterAction 触发异常', err && err.message ? err.message : err);
        }
    }

    // viewmodel 销毁时自动解绑（可选，防御性监听 destroy 事件）
    if (viewmodel && typeof viewmodel.on === 'function') {
        (function (vm, regId, nk) {
            var onDestroy = function () {
                for (var i = 0; i < _shortcutRegistry.length; i++) {
                    if (_shortcutRegistry[i].id === regId) {
                        _shortcutRegistry.splice(i, 1);
                        break;
                    }
                }
                if (_debounceTimers[nk]) {
                    clearTimeout(_debounceTimers[nk]);
                    _debounceTimers[nk] = null;
                }
            };
            try {
                vm.on('destroy', onDestroy);
            } catch (err) {
                ttx.console.warn('[shortcut] 监听 viewmodel destroy 失败', err && err.message ? err.message : err);
            }
        })(viewmodel, id, normalizedKeys);
    }

    ttx.console.log('[shortcut] 注册快捷键：' + keys + ' -> ' + normalizedKeys + (name ? ' (' + name + ')' : ''));
    return id;
};

/**
 * 获取所有快捷键
 * @param {object} [viewmodel] 不传获取所有；传入则只获取该 VM 下的快捷键
 * @returns {Array} [{ keys, callback, showName, groupId, viewmodel }]
 */
ttx.shortcut.getAllShortCut = function (viewmodel) {
    var result = [];
    for (var i = 0; i < _shortcutRegistry.length; i++) {
        var item = _shortcutRegistry[i];
        if (viewmodel && item.viewmodel !== viewmodel) continue;
        result.push({
            keys: item.keys,
            callback: item.callback,
            showName: item.options && item.options.showName,
            groupId: item.options && item.options.groupId,
            viewmodel: item.viewmodel
        });
    }
    return result;
};

/**
 * 解绑快捷键
 * @param {string} keys 注册时的快捷键字符串
 * @param {object} [viewmodel] 关联的 ViewModel
 * @param {string} [name] 可选，回调函数名（用于区分同一 keys 的多个回调）
 * @returns {number} 实际解绑的条数
 */
ttx.shortcut.unRegisterAction = function (keys, viewmodel, name) {
    var normalizedKeys = normalizeKeys(keys);
    if (!normalizedKeys) return 0;

    var removed = 0;
    for (var i = _shortcutRegistry.length - 1; i >= 0; i--) {
        var item = _shortcutRegistry[i];
        if (item.normalizedKeys !== normalizedKeys) continue;
        if (viewmodel && item.viewmodel !== viewmodel) continue;
        if (name && (item.name !== name)) continue;

        _shortcutRegistry.splice(i, 1);
        removed++;

        // 同步注销 ttx.command 中的同名事件
        if (ttx.command && typeof ttx.command.unregister === 'function') {
            try {
                ttx.command.unregister(item.normalizedKeys);
            } catch (err) {
                ttx.console.warn('[shortcut] 注销 command 失败', err && err.message ? err.message : err);
            }
        }
    }

    if (_debounceTimers[normalizedKeys] && removed > 0) {
        clearTimeout(_debounceTimers[normalizedKeys]);
        _debounceTimers[normalizedKeys] = null;
    }

    // 解绑后触发 viewmodel 的 afterShortcutUnRegisterAction 事件
    if (viewmodel && typeof viewmodel.execute === 'function') {
        try {
            viewmodel.execute('afterShortcutUnRegisterAction', { keys: keys, normalizedKeys: normalizedKeys });
        } catch (err) {
            ttx.console.error('[shortcut] afterShortcutUnRegisterAction 触发异常', err && err.message ? err.message : err);
        }
    }

    ttx.console.log('[shortcut] 解绑快捷键：' + keys + '，共 ' + removed + ' 条');
    return removed;
};

/**
 * 配置项（通常在 afterLoadMeta 事件中调用）
 * @param {object} viewmodel 关联的 ViewModel
 * @param {string} key 配置键：'unfocusFirstElement'（所有元素首次不聚焦）/ 'focusTipsFirstElement'（聚焦浮层类组件）
 * @param {any} value 配置值
 */
ttx.shortcut.setConfig = function (viewmodel, key, value) {
    if (!viewmodel || !key) return;
    // 用 viewmodel 上的 _shortcutId 作为存储键（VM 为对象引用，直接作属性键不可靠）
    var storeKey = viewmodel._shortcutId || (viewmodel._shortcutId = 'vm-' + (_shortcutSeed++));
    if (!_shortcutConfig[storeKey]) _shortcutConfig[storeKey] = {};
    _shortcutConfig[storeKey][key] = value;
    ttx.console.log('[shortcut] setConfig: ' + key + ' = ' + value);
};

/**
 * 读取配置项（内部/外部均可使用）
 * @param {object} viewmodel
 * @param {string} key
 * @returns {any}
 */
ttx.shortcut.getConfig = function (viewmodel, key) {
    if (!viewmodel || !key) return undefined;
    var storeKey = viewmodel._shortcutId;
    if (!storeKey || !_shortcutConfig[storeKey]) return undefined;
    return _shortcutConfig[storeKey][key];
};

/**
 * 显示快捷键帮助弹窗（列出所有快捷键）
 * @param {object} [viewmodel] 传入则只展示该 VM 下的快捷键
 */
ttx.shortcut.showHelp = function (viewmodel) {
    var list = ttx.shortcut.getAllShortCut(viewmodel);
    if (!list.length) {
        ttx.toast && ttx.toast.info && ttx.toast.info('暂无已注册的快捷键');
        return;
    }

    // 按分组组织
    var groups = {};
    list.forEach(function (item) {
        var gid = item.groupId == null ? 0 : item.groupId;
        if (!groups[gid]) groups[gid] = [];
        groups[gid].push(item);
    });

    var wrapper = document.createElement('div');
    wrapper.style.cssText = 'padding:0;';

    Object.keys(groups).sort(function (a, b) { return Number(a) - Number(b); }).forEach(function (gid) {
        var gTitle = document.createElement('div');
        gTitle.style.cssText = 'font-size:13px;font-weight:600;color:#333;margin:8px 0 4px;';
        gTitle.textContent = '分组 ' + gid;
        wrapper.appendChild(gTitle);

        var rows = groups[gid];
        rows.forEach(function (r) {
            var row = document.createElement('div');
            row.style.cssText = 'display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px dashed #f0f0f0;';
            var nameEl = document.createElement('span');
            nameEl.style.cssText = 'color:#333;font-size:13px;';
            nameEl.textContent = r.showName || '(未命名)';
            var keyEl = document.createElement('span');
            keyEl.style.cssText = 'color:#165dff;font-size:12px;font-family:Monaco,Consolas,monospace;background:#f0f5ff;padding:2px 8px;border-radius:3px;';
            keyEl.textContent = r.keys;
            row.appendChild(nameEl);
            row.appendChild(keyEl);
            wrapper.appendChild(row);
        });
    });

    if (ttx.modal && typeof ttx.modal.open === 'function') {
        ttx.modal.open({
            title: '快捷键帮助',
            content: wrapper,
            width: 480,
            showFooter: false,
            closeOnMask: true
        });
    }
};

// 模块初始化时安装全局监听器
installGlobalListener();
